"use client"

import { HStack, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import { type ColorPaletteBlock as ColorPaletteBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface ColorPaletteBlockProps extends ContentBlockTitleNumberProps {
  block: ColorPaletteBlockType
}

const ColorPaletteBlock: FC<ColorPaletteBlockProps> = ({
  block,
  titleNumber,
}) => {
  const {
    color,
    darker_bg,
    legend,
    small_thumb,
    subtitle,
    title,
    two_columns_palette,
  } = block
  // Ensure colors is an array
  const colors = Array.isArray(color) ? color : [color]
  const trimmedTitle = title?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    subtitle !== undefined || subtitle !== "" || subtitle !== null
  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="color-palette-block internal-padding"
      direction={{ base: "column", lg: two_columns_palette ? "row" : "column" }}
      gap={{ base: 8, lg: two_columns_palette ? 6 : 8 }}
    >
      {hasTitle && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: block.two_columns_palette
              ? block.title !== undefined
                ? "30%"
                : "100%"
              : "100%",
          }}
          {...getStickyContentBlockLeftProps(two_columns_palette)}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle
              fontWeight="bold"
              title={trimmedTitle}
              titleNumber={titleNumber}
            />
            {hasDescription && (
              <Text as="p" color={"fg.muted"} fontFamily="mono" fontSize="sm">
                {subtitle}
              </Text>
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: block.two_columns_palette
            ? block.title !== undefined
              ? "70%"
              : "100%"
            : "100%",
        }}
        pl={!hasTitle && two_columns_palette ? { base: 0, lg: 12 } : 0}
      >
        <BlurFade inView>
          <HStack
            alignItems="flex-start"
            bgColor="bg.subtle"
            borderColor="border"
            borderRadius="3xl"
            borderWidth="1px"
            flexBasis={small_thumb ? "auto" : undefined}
            flexGrow={small_thumb ? 1 : undefined}
            flexShrink={small_thumb ? 1 : undefined}
            gap={4}
            mb={2}
            p={6}
            width="100%"
            wrap="wrap"
          >
            {colors.map((color) => (
              <Stack key={`${color.name}-${color.hex}`}>
                {small_thumb && (
                  <HStack
                    alignItems="center"
                    bgColor="bg.paper"
                    borderRadius="xl"
                    boxShadow="lg"
                    className="small_thumb"
                    gap={{ base: 2, md: 2 }}
                    px={{ base: 2, md: 2 }}
                    py={{ base: 2, md: 2 }}
                    width="100%"
                  >
                    <VStack
                      alignItems="center"
                      bgColor={color.hex}
                      border={{
                        base: "1px solid {colors.gray.200}",
                        _dark: "1px solid {colors.gray.700}",
                      }}
                      borderRadius="md"
                      flexShrink={0}
                      height={{ base: "42px", md: "50px" }}
                      justifyContent="center"
                      width={{ base: "42px", md: "50px" }}
                    />
                    <VStack
                      alignItems="flex-start"
                      flex="1"
                      gap={{ base: 1, md: 2 }}
                    >
                      <Text
                        color="fg.default"
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="medium"
                        lineHeight="1"
                        pr={2}
                      >
                        {color.name}
                      </Text>
                      <Text
                        color="fg.muted"
                        fontFamily="code"
                        fontSize={{ base: "xs", md: "sm" }}
                        lineHeight="1"
                        pr={2}
                      >
                        {color.hex}
                      </Text>
                    </VStack>
                  </HStack>
                )}
                {!small_thumb && (
                  <VStack
                    _hover={{
                      transform: "scale(1.05)",
                    }}
                    alignItems="center"
                    bgColor={"bg.paper"}
                    borderRadius="lg"
                    boxShadow="md"
                    flexShrink={0}
                    gap={0}
                    height={{ base: "120px", md: "160px" }}
                    overflow="hidden"
                    transition="transform 0.2s ease-in-out"
                    width={{ base: "120px", md: "160px" }}
                  >
                    <VStack
                      alignItems={"center"}
                      bgColor={color.hex}
                      flex="1"
                      flexGrow={1}
                      gap={1}
                      justifyContent={"center"}
                      minHeight={"60px"}
                      w="full"
                    >
                      <Text
                        color="whiteAlpha.900"
                        fontFamily="code"
                        fontSize="lg"
                        mixBlendMode="screen"
                      >
                        {color.contrast_ratio}
                      </Text>
                    </VStack>
                    <VStack
                      alignItems="flex-start"
                      bgColor="white"
                      flex="1"
                      flexGrow={0}
                      gap={0}
                      p={2}
                      w="full"
                    >
                      <Text
                        color="gray.700"
                        fontSize="sm"
                        fontWeight="semibold"
                      >
                        {color.name}
                      </Text>
                      <Text color="fg.muted" fontSize="sm">
                        {color.hex}
                      </Text>
                    </VStack>
                  </VStack>
                )}
              </Stack>
            ))}
          </HStack>
        </BlurFade>

        {legend && (
          <BlurFade flexDirection="column" inView>
            <MarkdownParser fontSize="md" markdown={legend} />
          </BlurFade>
        )}
      </VStack>
    </Stack>
  )
}

export default ColorPaletteBlock
